/**
 * Generated With: pnpm x packages/api/tools/generate-shopify-rest-model.ts https://shopify.dev/api/admin/rest/reference/inventory/inventoryitem
 * API Version: 2022-01
 */

import { FieldType } from "../../type-system/FieldType";
import { shopifyCountryCodeEnumOptions } from "./constants";
import {
  ShopifyFulfillmentOrderLineItemKey,
  ShopifyFulfillmentOrderLineItemToShopifyInventoryItemKey,
  ShopifyInventoryItemKey,
  ShopifyInventoryItemToFulfillmentOrderLineItemKey,
  ShopifyInventoryItemToInventoryQuantitiesKey,
  ShopifyInventoryItemToLocationsKey,
  ShopifyInventoryItemToProductVariantKey,
  ShopifyInventoryItemToProductVariantsKey,
  ShopifyInventoryItemToShopKey,
  ShopifyInventoryLevelKey,
  ShopifyInventoryLevelToInventoryItemKey,
  ShopifyInventoryLevelToLocationKey,
  ShopifyInventoryQuantityKey,
  ShopifyInventoryQuantityToInventoryItemKey,
  ShopifyLocationKey,
  ShopifyLocationToInventoryItemsKey,
  ShopifyProductVariantKey,
  ShopifyProductVariantToInventoryItemKey,
  ShopifyShopKey,
} from "./keys";
import { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifyInventoryItemKey,
  defaultFilterableApiIdentifiers: ["sku", "tracked"],
  "2022-01": {
    name: "Shopify Inventory Item",
    fields: [
      {
        name: "Cost",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "cost" } },
      },
      {
        name: "Country Code Of Origin",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "country_code_of_origin" } },
      },
      {
        name: "Country Harmonized System Codes",
        fieldType: FieldType.JSON,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "country_harmonized_system_codes" } },
      },
      {
        name: "Harmonized System Code",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "harmonized_system_code" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id" } },
      },
      {
        name: "Locations",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyInventoryItemToLocationsKey,
        configuration: {
          joinModelKey: ShopifyInventoryLevelKey,
          relatedModelKey: ShopifyLocationKey,
          inverseJoinModelFieldKey: ShopifyInventoryLevelToInventoryItemKey,
          inverseRelatedModelFieldKey: ShopifyInventoryLevelToLocationKey,
          inverseFieldKey: ShopifyLocationToInventoryItemsKey,
        },
      },
      {
        name: "Product Variant",
        fieldType: FieldType.HasOne,
        key: ShopifyInventoryItemToProductVariantKey,
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
          inverseFieldKey: ShopifyProductVariantToInventoryItemKey,
        },
      },
      {
        name: "Province Code Of Origin",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "province_code_of_origin" } },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "requires_shipping" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "created_at" } },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "updated_at" } },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "sku" } },
      },
      {
        name: "Tracked",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "tracked" } },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifyInventoryItemToShopKey,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "shop_id" } },
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    syncConfig: { type: "custom", resource: "inventory" },
  },
  "2023-04": {
    fields: [
      {
        name: "Fulfillment Order Line Item",
        fieldType: FieldType.HasMany,
        key: ShopifyInventoryItemToFulfillmentOrderLineItemKey,
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderLineItemKey,
          inverseFieldKey: ShopifyFulfillmentOrderLineItemToShopifyInventoryItemKey,
        },
      },
    ],
  },
  "2023-07": {
    fields: [
      {
        name: "Duplicate Sku Count",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_duplicate_sku_count",
            graphQLFieldKey: "duplicateSkuCount",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Legacy Resource Id",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_legacy_resource_id",
            graphQLFieldKey: "legacyResourceId",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Tracked Editable",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_tracked_editable",
            graphQLFieldKey: "trackedEditable",
            graphQLSelection: `
            locked
            reason
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Unit Cost",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_unit_cost",
            graphQLFieldKey: "unitCost",
            graphQLSelection: `
            amount
            currencyCode
            `,
            graphQLOnlyField: true,
          },
        },
      },
    ],
  },
  "2023-10": {
    fields: [
      {
        name: "Inventory History Url",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_inventory_history_url",
            graphQLFieldKey: "inventoryHistoryUrl",
            graphQLOnlyField: true,
          },
        },
      },
    ],
  },
  "2024-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { shopifyFieldKey: "id", graphQLFieldKey: "id" } },
      },
      {
        name: "Measurement",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_measurement",
            graphQLFieldKey: "measurement",
            graphQLSelection: `
            id
            weight {
              unit
              value
            }
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Weight Value",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "weight_value",
          },
        },
      },
      {
        name: "Weight Unit",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "weight_unit",
          },
        },
      },
    ],
  },
  "2025-04": {
    graphQLTypename: "InventoryItem",
    fields: [
      {
        name: "Cost",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "cost",
            graphQLFieldKey: "cost",
            skipGraphQLSelection: true,
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Country Code Of Origin",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "country_code_of_origin",
            graphQLFieldKey: "countryCodeOfOrigin",
            isShopifyEnum: true,
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
        configuration: {
          allowOther: true,
          allowMultiple: false,
          options: shopifyCountryCodeEnumOptions,
        },
      },
      {
        name: "Country Harmonized System Codes",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "country_harmonized_system_codes",
            graphQLFieldKey: "countryHarmonizedSystemCodes",
            skipGraphQLSelection: true,
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Harmonized System Code",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "harmonized_system_code",
            graphQLFieldKey: "harmonizedSystemCode",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "id",
            graphQLFieldKey: "id",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Locations",
        fieldType: FieldType.HasManyThrough,
        key: ShopifyInventoryItemToLocationsKey,
        configuration: {
          joinModelKey: ShopifyInventoryLevelKey,
          relatedModelKey: ShopifyLocationKey,
          inverseJoinModelFieldKey: ShopifyInventoryLevelToInventoryItemKey,
          inverseRelatedModelFieldKey: ShopifyInventoryLevelToLocationKey,
          inverseFieldKey: ShopifyLocationToInventoryItemsKey,
        },
      },
      {
        name: "Product Variant",
        fieldType: FieldType.HasOne,
        key: ShopifyInventoryItemToProductVariantKey,
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
          inverseFieldKey: ShopifyProductVariantToInventoryItemKey,
        },
      },
      {
        name: "Province Code Of Origin",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "province_code_of_origin",
            graphQLFieldKey: "provinceCodeOfOrigin",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Requires Shipping",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "requires_shipping",
            graphQLFieldKey: "requiresShipping",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "created_at",
            graphQLFieldKey: "createdAt",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Shopify Updated At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "updated_at",
            graphQLFieldKey: "updatedAt",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "SKU",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "sku",
            graphQLFieldKey: "sku",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Tracked",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "tracked",
            graphQLFieldKey: "tracked",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
          },
        },
      },
      {
        name: "Fulfillment Order Line Item",
        fieldType: FieldType.HasMany,
        key: ShopifyInventoryItemToFulfillmentOrderLineItemKey,
        configuration: {
          relatedModelKey: ShopifyFulfillmentOrderLineItemKey,
          inverseFieldKey: ShopifyFulfillmentOrderLineItemToShopifyInventoryItemKey,
        },
      },
      {
        name: "Duplicate Sku Count",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_duplicate_sku_count",
            graphQLFieldKey: "duplicateSkuCount",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Legacy Resource Id",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_legacy_resource_id",
            graphQLFieldKey: "legacyResourceId",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Tracked Editable",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_tracked_editable",
            graphQLFieldKey: "trackedEditable",
            graphQLSelection: `
            locked
            reason
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Unit Cost",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_unit_cost",
            graphQLFieldKey: "unitCost",
            graphQLSelection: `
            amount
            currencyCode
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Inventory History Url",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_inventory_history_url",
            graphQLFieldKey: "inventoryHistoryUrl",
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Measurement",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_inventory_item_measurement",
            graphQLFieldKey: "measurement",
            graphQLSelection: `
            id
            weight {
              unit
              value
            }
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Locations Count",
        fieldType: FieldType.JSON,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "__gadget_graphql_locations_count",
            graphQLFieldKey: "locationsCount",
            graphQLSelection: `
            count
            precision
            `,
            graphQLOnlyField: true,
          },
        },
      },
      {
        name: "Inventory Quantities",
        fieldType: FieldType.HasMany,
        key: ShopifyInventoryItemToInventoryQuantitiesKey,
        configuration: {
          relatedModelKey: ShopifyInventoryQuantityKey,
          inverseFieldKey: ShopifyInventoryQuantityToInventoryItemKey,
        },
      },
      {
        name: "Weight Value",
        fieldType: FieldType.Number,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "weight_value",
            graphQLFieldKey: "weightValue",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
            skipGraphQLSelection: true,
            deprecated: true,
          },
        },
      },
      {
        name: "Weight Unit",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            shopifyFieldKey: "weight_unit",
            graphQLFieldKey: "weightUnit",
            availableInWebhook: ["inventory_items/create", "inventory_items/update"],
            skipGraphQLSelection: true,
            deprecated: true,
          },
        },
      },
    ],
    syncConfig: {
      type: "graphql_list",
      resource: "inventoryItems",
      filters: {
        updatedAt: { sortKey: "UPDATED_AT" },
        createdAt: { sortKey: "CREATED_AT" },
      },
    },
  },
  "2026-01": {
    fields: [
      {
        name: "Product Variant",
        fieldType: FieldType.HasOne,
        key: ShopifyInventoryItemToProductVariantKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            deprecated: true,
          },
        },
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
          inverseFieldKey: ShopifyProductVariantToInventoryItemKey,
        },
      },
      {
        name: "Product Variants",
        fieldType: FieldType.HasMany,
        key: ShopifyInventoryItemToProductVariantsKey,
        configuration: {
          relatedModelKey: ShopifyProductVariantKey,
          inverseFieldKey: ShopifyProductVariantToInventoryItemKey,
        },
      },
      {
        name: "Inventory Quantities",
        fieldType: FieldType.HasMany,
        key: ShopifyInventoryItemToInventoryQuantitiesKey,
        configuration: {
          relatedModelKey: ShopifyInventoryQuantityKey,
          inverseFieldKey: ShopifyInventoryQuantityToInventoryItemKey,
        },
        isDeleted: true,
      },
    ],
  },
};

export const ShopifyInventoryItem = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint?: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
